import FileSaver from 'file-saver'
import moment from 'moment'
import { ApolloQueryResult, gql } from '@apollo/client'
import ApolloClient from 'apollo-boost'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { customAlphabet } from 'nanoid'

import { TOKEN, GRAPHQL_API_BASE_DOMAIN, nanoIdCharacters } from './constants'
import { getLocalItem } from '../local-client/local-client'
import Make from './make'
import { getKeyFromMetricValue } from '../helpers'

const make = Make('', true)

const client = new ApolloClient({
  uri: `${GRAPHQL_API_BASE_DOMAIN}graphql`,
  cache: new InMemoryCache({
    addTypename: false,
  }),
  // onError: () => {},
  request: (operation) => {
    const token = getLocalItem(TOKEN)
    if (
      process.env.REACT_APP_STAGING_OVERRIDE === 'true' &&
      process.env.REACT_APP_NODE_ENV !== 'production'
    ) {
      operation.setContext({
        headers: {
          'override-staging-database': 'true',
          authorization: token ? `Bearer ${token}` : '',
        },
      })
    } else {
      operation.setContext({
        headers: {
          authorization: token ? `Bearer ${token}` : '',
        },
      })
    }
  },
})

export const getDimensionsString = (selectedDimensions) => {
  const keys = Object.keys(selectedDimensions)
  if (keys.length === 0) {
    return ''
  }
  let dimensionFilters = ''
  keys.forEach((key) => {
    if (selectedDimensions[key].length > 0) {
      dimensionFilters += `{dimensionName: "${key}", dimensionOptions: ${JSON.stringify(
        [...selectedDimensions[key]],
      )}}`
    }
  })
  return dimensionFilters !== ''
    ? `filterList: { dimensionFilters: [${dimensionFilters}]}`
    : ''
}

export const getDateRangeString = (currentDate, startDate, endDate) => {
  if (currentDate === 'custom') {
    const from = moment(startDate).format('YYYY-MM-DD')
    const to = moment(endDate).format('YYYY-MM-DD')
    return `startDate: "${from}", endDate: "${to}",`
  }
  return `startDate: "${currentDate}",`
}

export const getStoredCodesStats = async (): Promise<any> => {
  const query = gql`
    query GetStoredCodesStats {
      storedCodeStats {
        codeIDs
        metricValues {
          displayName
          helpText
          metricID
          mf30
          mf365
          mf30Partial
          mf365Partial
          units
        }
        status
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

export const getSettings = async (): Promise<any> => {
  const query = gql`
    query GetSettings {
      currentAccount {
        qrSettings {
          enabled
          value
          name
        }
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

interface GetShortLinkCandidatesProps {
  links: number
}

export const getShortLinkCandidates = async ({
  links,
}: GetShortLinkCandidatesProps): Promise<any> => {
  const query = gql`
    query GetShortLinkCandidates {
      shortLinkCandidates(nLinks: ${links}) {
        availableLinkIDs
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

export const getCampaignLinkDashboardMeta = async (): Promise<any> => {
  const query = gql`
    query GetCampaignLinkDashboardMeta {
      campaignLinkDashboardMeta {
        availableDimensions {
          dimensionName
          dimensionOptions
          helpText
        }
        availableClickMetrics {
          calculation
          description
          displayName
          increasePositive
          isStandard
          isUplifterClickMetric
          metricID
          setupComplete
          units
        }
        availableMetrics {
          isStandard
          increasePositive
          isUplifterClickMetric
          calculation
          description
          displayName
          metricID
          setupComplete
          units
        }
      }
    }
  `
  return client.query({ query })
}

export const getCampaignLinkDashboardMetaDefaults = async (): Promise<any> => {
  const query = gql`
    query GetCampaignLinkDashboardMetaDefaults {
      campaignCodeGenerator {
        masterPrefix
        paramSeparator
      }
      campaignLinkDashboardMeta {
        onloadDefaultOptions {
          enabled
          value
          name
        }
        dataSourceDescription
        availableClickMetrics {
          calculation
          description
          displayName
          increasePositive
          isStandard
          isUplifterClickMetric
          metricID
          setupComplete
          units
        }
        availableDimensions {
          dimensionName
          dimensionOptions
          helpText
        }
        availableMetrics {
          isStandard
          increasePositive
          isUplifterClickMetric
          calculation
          description
          displayName
          metricID
          setupComplete
          units
        }
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

interface GetCampaignLinkDashboardTableProps {
  selectedDimensions: any[]
  successMetric: string
  currentDate: string
  startDate: string
  endDate: string
  matchType: string
}

export const getCampaignLinkDashboardTable = async ({
  matchType,
  selectedDimensions,
  successMetric,
  currentDate,
  startDate,
  endDate,
}: GetCampaignLinkDashboardTableProps): Promise<any> => {
  const filtersString = getDimensionsString(selectedDimensions)
  const dateRangeString = getDateRangeString(currentDate, startDate, endDate)
  // tableData {
  //   campaignLink
  //   metricValue
  // }
  const query = gql`
    query GetCampaignLinkDashboardTable {
      campaignLinkDashboardTable(
        ${dateRangeString}
        selectedMetric: "${successMetric}",
        matchType: "${matchType}",
        ${filtersString}
      ) {
        campaignLinks
        tableData {
          helpText
          metricID
          metricName
          metricValue
          units
        }
      }
    }
  `
  return client.query({ query })
}

interface GetCampaignLinkDashboardGraphProps {
  selectedDimensions: any[]
  successMetric: string[]
  breakdownDimension: string
  currentDate: string
  startDate: string
  endDate: string
  granularity: string
  matchType: string
}

export const getCampaignLinkDashboardGraph = async ({
  selectedDimensions,
  successMetric,
  breakdownDimension,
  currentDate,
  startDate,
  endDate,
  granularity,
  matchType,
}: GetCampaignLinkDashboardGraphProps): Promise<any> => {
  const filtersString = getDimensionsString(selectedDimensions)
  const dims =
    breakdownDimension !== ''
      ? `stackDimension: "${breakdownDimension}",`
      : 'stackDimension: "",'
  // granularity: quarterly, monthly, weekly, daily
  const dateRangeString = getDateRangeString(currentDate, startDate, endDate)
  const useMetric = Array.isArray(successMetric)
    ? [...successMetric]
    : [successMetric]
  let accountQuery = ``
  useMetric.forEach((metric) => {
    accountQuery += `
    ${getKeyFromMetricValue(metric)}:campaignLinkDashboardGraph(
        ${dateRangeString}
        granularity: "${granularity}",
        ${dims}
        selectedMetric: "${metric}",
        matchType: "${matchType}",
        ${filtersString}
      ) {
        dateRange,
        graphType,
        graphData {
          dimensionName,
          dimensionValues
        }
      }
    `
  })
  const query = gql`
    query GetCampaignLinkDashboardGraph {
      ${accountQuery}
    }
  `
  return client.query({ query })
}

interface GetCampaignLinkDashboardGraphClickDataProps {
  selectedDimensions: any[]
  successMetric: string
  breakdownDimension: string
  currentDate: string
  startDate: string
  endDate: string
  granularity: string
}

export const getCampaignLinkDashboardGraphClickData = async ({
  selectedDimensions,
  successMetric,
  breakdownDimension,
  currentDate,
  startDate,
  endDate,
  granularity,
}: GetCampaignLinkDashboardGraphClickDataProps): Promise<any> => {
  const filtersString = getDimensionsString(selectedDimensions)
  const dims =
    breakdownDimension !== ''
      ? `stackDimension: "${breakdownDimension}",`
      : 'stackDimension: "",'
  // granularity: quarterly, monthly, weekly, daily
  const dateRangeString = getDateRangeString(currentDate, startDate, endDate)
  const query = gql`
    query GetCampaignLinkDashboardGraphClickData {
      campaignLinkDashboardGraphClickData(
        ${dateRangeString}
        granularity: "${granularity}",
        ${dims}
        selectedMetric: "${successMetric}",
        ${filtersString}
      ) {
        dateRange,
        graphType,
        graphData {
          dimensionName,
          dimensionValues
        }
      }
    }
  `
  return client.query({ query })
}

interface GetCampaignLinkDashboardMetricDataProps {
  selectedDimensions: any[]
  successMetric: string
  currentDate: string
  startDate: string
  endDate: string
  granularity: string
  matchType: string
}

export const getCampaignLinkDashboardMetricData = async ({
  selectedDimensions,
  successMetric,
  currentDate,
  startDate,
  endDate,
  granularity,
  matchType,
}: GetCampaignLinkDashboardMetricDataProps): Promise<any> => {
  const filtersString = getDimensionsString(selectedDimensions)
  const dateRangeString = getDateRangeString(currentDate, startDate, endDate)
  const dataKeys = `
    metricDisplay
    metricLastCompleteValue
    metricPrevValue
    metricTitle
    metricValue`
  const query = gql`
    query GetCampaignLinkDashboardMetricData {
      campaignLinkDashboardMetricData(
        ${dateRangeString}
        granularity: "${granularity}",
        selectedMetric: "${successMetric}",
        matchType: "${matchType}",
        ${filtersString}
      ) {
        lastYearStartDate
        lastPeriodStartDate
        prevPeriodStartDate
        increasePositive
        periodComparisonVsPeriodAverage {
          ${dataKeys}
        }
        metricLastPeriod {
          ${dataKeys}
        }
        metricLastPeriodComparison {
          ${dataKeys}
        }
        metricPeriodOnPeriodComparison {
          ${dataKeys}
        }
        totalMetric {
          ${dataKeys}
        }
      }
    }
  `

  return client.query({ query })
}

interface GetCampaignLinkDashboardMetricClickDataProps {
  selectedDimensions: any[]
  successMetric: string
  currentDate: string
  startDate: string
  endDate: string
  granularity: string
}

export const getCampaignLinkDashboardMetricClickData = async ({
  selectedDimensions,
  successMetric,
  currentDate,
  startDate,
  endDate,
  granularity,
}: GetCampaignLinkDashboardMetricClickDataProps): Promise<any> => {
  const filtersString = getDimensionsString(selectedDimensions)
  const dateRangeString = getDateRangeString(currentDate, startDate, endDate)
  const dataKeys = `
    metricDisplay
    metricLastCompleteValue
    metricPrevValue
    metricTitle
    metricValue`
  const query = gql`
    query GetCampaignLinkDashboardMetricDataClickData {
      campaignLinkDashboardMetricDataClickData(
        ${dateRangeString}
        granularity: "${granularity}",
        selectedMetric: "${successMetric}",
        ${filtersString}
      ) {
        lastYearStartDate
        lastPeriodStartDate
        prevPeriodStartDate
        increasePositive
        periodComparisonVsPeriodAverage {
          ${dataKeys}
        }
        metricLastPeriod {
          ${dataKeys}
        }
        metricLastPeriodComparison {
          ${dataKeys}
        }
        metricPeriodOnPeriodComparison {
          ${dataKeys}
        }
        totalMetric {
          ${dataKeys}
        }
      }
    }
  `

  return client.query({ query })
}

export const getUserPreferredHomepageGQL = async (): Promise<any> => {
  const query = gql`
    query GetUserPreferredHomepageGQL {
      currentUser {
        userID
        preferredHomepage
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

export const getUserDetailsGQL = async (): Promise<any> => {
  const query = gql`
    query GetUserDetailsGQL {
      currentUser {
        telNum
        currentAccountPermission
        acceptedMarketing
        userID
        profileImgLink
        userAccountProfiles {
          accountID
          accountName
          companyID
          companyName
          userPermission
          homepage
        }
        email
        firstName
        lastName
        userName
        publicJobTitle
        preferredHomepage
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

export const getUserAccountDetails = async (): Promise<any> => {
  const query = gql`
    query GetUserAccountDetails {
      currentAccount {
        qrCodeLogoList
        accountID
        auditAvailable
        accountName
        blankAccount
        companyID
        companyName
        created
        explainAvailable
        homepage
        isDemo
        logoLink
        reportAvailable
        trackAvailable
        uplifterAccountLandingPage
        qrSettings {
          enabled
          value
          name
        }
      }
      currentUser {
        telNum
        currentAccountPermission
        acceptedMarketing
        userID
        userAccountProfiles {
          accountID
          accountName
          companyID
          companyName
          userPermission
          homepage
        }
        profileImgLink
        email
        firstName
        lastName
        userName
        publicJobTitle
        preferredHomepage
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

interface AddUserToAccountProps {
  users: string[]
  accounts: string[]
  permission: string
}

export const addUserToAccount = async ({
  users,
  accounts,
  permission,
}: AddUserToAccountProps): Promise<any> => {
  let accountQuery = ``
  const nanoid = customAlphabet(nanoIdCharacters, 10)
  users.forEach((user, userIndex) => {
    accounts.forEach((account, accountIndex) => {
      accountQuery += `
        ${nanoid()}:addUserAccount(
            accountID: "${account}",
            email: "${user}",
            permissionLevel: "${permission}",
        ) {
            inviteOpen,
            email
        }
      `
    })
  })
  const mutation = gql`
  mutation AddUserToAccount {
      ${accountQuery}
    }
  `
  return client.mutate({ mutation })
}

interface SetDashboardSettingsProps {
  additionalMetrics?: any
  onloadDefaultOptions?: any
}

export const setDashboardSettings = async ({
  additionalMetrics = null,
  onloadDefaultOptions = null,
}: SetDashboardSettingsProps): Promise<any> => {
  let updateString = ''
  if (onloadDefaultOptions) {
    const { name, enabled, value } = onloadDefaultOptions[0]
    updateString += `onloadDefaultOptions: {name: "${name}", enabled: ${enabled}, value: "${value}"}`
  }
  if (additionalMetrics) {
    const {
      metricID,
      increasePositive,
      displayName,
      description,
      units,
      calculation,
    } = additionalMetrics
    const useCalc = calculation || []
    updateString += updateString !== '' ? ', ' : ''
    updateString += `additionalMetrics: [{metricID: "${metricID}", increasePositive: ${increasePositive}, displayName: "${displayName}", description: "${description}", units: "${units}", calculation: ${JSON.stringify(
      useCalc,
    )}}]`
  }

  const mutation = gql`
  mutation SetDashboardSettings {
      setLinkPerformanceDashboardAccountSettings(${updateString}) {
        dashboardTitle
        availableMetrics {
          isStandard
          calculation
          description
          displayName
          increasePositive
          metricID
          setupComplete
          units
        }
        onloadDefaultOptions {
          enabled
          name
          value
        }
      }
    }
  `
  return client.mutate({ mutation })
}

interface CampaignLinkRawData {
  startDate: string
  endDate: string
  granularity: string
  selectedMetric: string
  stackDimension: string
  applyFilters: {
    dimensionName: string
    dimensionOptions: string[]
  }[]
}

export const getCampaignLinkRawData = async (
  data: CampaignLinkRawData,
): Promise<boolean> => {
  // eslint-disable-next-line
  // debugger
  const req = await make.post({
    endpoint: `report/campaign-link-dashboard-raw-data`,
    data,
    options: {
      ...data,
      headers: {
        'Content-Type':
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      responseType: 'arraybuffer',
    },
  })

  if (!req.success) {
    return false
  }

  if (req.res.data) {
    const blob = new Blob([new Uint8Array(req.res.data)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const now = new Date(Date.now())
    await FileSaver.saveAs(
      blob,
      `${moment(now).format('YYYY-MM-DD')} Link Performance Export.xlsx`,
    )
  }

  return true
}

export const getDataSourceMetricList = async (): Promise<any> => {
  const query = gql`
    query getDataSourceMetricList {
      dataSourceMetricList {
        dataSourceMetricList {
          description
          calculation
          increasePositive
          displayName
          metricID
          requiredMetrics
          units
        }
      }
    }
  `
  return client.query({ query })
}

export const getUserList = async (): Promise<any> => {
  const query = gql`
    query GetUserList {
      currentCompany {
        accountList {
          userAccountProfiles {
            lastLogin
            userEmail
            userID
            userPermission
            lastUpdated
          }
          accountID
          accountName
        }
        companyID
        companyName
      }
    }
  `
  return client.query({ query })
}

interface UploadAccountLogo {
  file: File
}

export const uploadAccountLogo = async (
  data: UploadAccountLogo,
): Promise<boolean | any> => {
  const formData = new FormData()
  formData.append('file', data.file)

  const req = await make.post({
    endpoint: 'account/update-account-logo-image',
    options: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })

  if (req.success && req.res.status === 200) {
    return true
  }

  return req.res
}

interface UpdateUserImage {
  file: File
}

export const updateUserImage = async (
  data: UpdateUserImage,
): Promise<boolean | any> => {
  const formData = new FormData()
  formData.append('file', data.file)

  const req = await make.post({
    endpoint: 'account/upload-user-profile-pic',
    options: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })

  if (req.success && req.res.status === 200) {
    return true
  }

  return req.res
}

export const unsubscribeMarketing = async ({ uid }): Promise<boolean | any> => {
  const req = await make.get({
    endpoint: `account/unsubscribe-marketing?uid=${uid}`,
    // options: {
    //   'Content-Type': 'multipart/form-data',
    // },
    // data,
  })

  if (req.success && req.res.status === 200) {
    return true
  }

  return req.res
}

export const getQrLogoImage = async (
  blobName: string,
): Promise<boolean | any> => {
  const query = gql`
    query GetQrLogoImage($blobName: String!) {
      currentAccount {
        qrCodeLogoSrc(blobName: $blobName)
      }
    }
  `
  return client.query({ query, variables: { blobName } })
}

export const uploadQrLogo = async (
  data: UploadAccountLogo,
): Promise<boolean | any> => {
  const formData = new FormData()
  formData.append('file', data.file)

  const req = await make.post({
    endpoint: 'account/upload-qr-logo-image',
    options: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })

  if (req.success && req.res.status === 200) {
    return true
  }

  return req.res
}

export const deleteQrLogo = async (blobName: String): Promise<any> => {
  const mutation = gql`
    mutation DeleteQrCodeImage($blobName: String!) {
      deleteQrCodeImage(blobName: $blobName) {
        qrCodeLogoList
      }
    }
  `
  return client.mutate({
    mutation,
    variables: { blobName },
  })
}

export const getOnboardingSectionsProgress = async (): Promise<any> => {
  const query = gql`
    query GetOnboardingSections {
      userOnboardingProgress {
        userOnboardingProgressList {
          onboardingSectionID
          sectionCompleted
          sectionSkipped
          sectionSkippedBefore
        }
      }
      accountOnboardingProgress {
        accountOnboardingProgressList {
          onboardingSectionID
          sectionCompleted
          sectionSkipped
          sectionSkippedBefore
        }
      }
    }
  `
  return client.query({ query })
}

export const getCodesByUser = async (): Promise<any> => {
  const query = gql`
    query getCodesByUser {
      codesByUser {
        codeList {
          c
          cDef {
            fID
            n
          }
          cID
          e
          edB
          edT
          sL
          t
          vN
        }
        totalCodes
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

export const getMinCodesByAccount = async (): Promise<any> => {
  const query = gql`
    query getMinCodesByAccount {
      minCodesByAccount {
        author
        codeDef
        codeID
        createdTime
        minGenDef {
          paramID
          paramName
        }
        fullLink
        shortLink
        totalCodes
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

export const getCodesByAccount = async (): Promise<any> => {
  const query = gql`
    query getCodesByAccount {
      codesByAccount {
        codeList {
          c
          cDef {
            fID
            n
            v
          }
          cID
          edB
          e
          edT
          sL
          t
          vN
        }
        totalCodes
      }
    }
  `
  return client.query({ query, fetchPolicy: 'no-cache' })
}

export const addCodes = async ({ lP, cL }): Promise<any> => {
  const mutation = gql`
    mutation AddCodes($codeList: [AddCode!]!, $landingPage: String!) {
      addCodes(codeList: $codeList, landingPage: $landingPage) {
        codeList {
          c
          cDef {
            fID
            n
            v
          }
          cID
          e
          edB
          edT
          sL
          t
          vN
        }
      }
    }
  `
  return client.mutate({
    mutation,
    variables: { codeList: cL, landingPage: lP },
  })
}

export const updateCode = async ({
  codeID,
  fullCode,
  paramDefs,
}): Promise<any> => {
  const mutation = gql`
    mutation UpdateCode(
      $codeID: String!
      $fullCode: String
      $paramDefs: [ParamDefInput!]
    ) {
      updateCode(codeID: $codeID, fullCode: $fullCode, paramDefs: $paramDefs) {
        cDef {
          fID
          n
          v
        }
        c
        cID
        e
        edB
        edT
        sL
        t
        vN
      }
    }
  `
  return client.mutate({
    mutation,
    variables: { codeID, fullCode, paramDefs },
  })
}

export const deleteCode = async ({ codeID }): Promise<any> => {
  const mutation = gql`
    mutation DeleteCode($codeID: String!) {
      deleteCode(codeID: $codeID) {
        cDef {
          fID
          n
          v
        }
        c
        cID
        e
        edB
        edT
        sL
        t
        vN
      }
    }
  `
  return client.mutate({
    mutation,
    variables: { codeID },
    fetchPolicy: 'no-cache',
  })
}

export const updatePreferredHomepage = async (
  newPage: string,
): Promise<any> => {
  const mutation = gql`
    mutation UpdateUserHomepage($newPage: String!) {
      updateUserHomepage(preferredHomepage: $newPage) {
        preferredHomepage
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { newPage },
    fetchPolicy: 'no-cache',
  })
}

export const updateQrSettings = async ({ qrSettings }): Promise<any> => {
  const mutation = gql`
    mutation UpdateQrSettings($qrSettings: [GenericSettingInput!]!) {
      updateQrSettings(qrSettings: $qrSettings) {
        qrSettings {
          enabled
          value
          name
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { qrSettings },
    fetchPolicy: 'no-cache',
  })
}

export const updateMarketingPermission = async ({
  allowMarketing,
}): Promise<any> => {
  const mutation = gql`
    mutation UpdateMarketingPermission($allowMarketing: Boolean!) {
      userAccountSettings {
        updateMarketingPermission(allowMarketing: $allowMarketing) {
          acceptedMarketing
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { allowMarketing },
    fetchPolicy: 'no-cache',
  })
}

interface ShareCodesData {
  codeList: string[]
  emailList: string[]
  note: string
  subject: string
}

export const shareCodes = async (data: ShareCodesData): Promise<any> => {
  const { codeList, emailList, note, subject } = data
  const mutation = gql`
    mutation ShareCodes(
      $codeList: [String!]!
      $emailList: [String!]!
      $note: String!
      $subject: String!
    ) {
      track {
        shareCodes(
          codeList: $codeList
          emailList: $emailList
          note: $note
          subject: $subject
        ) {
          account
          cid
          codeShareData
          requester
          sent
          shortID
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { codeList, emailList, note, subject },
    fetchPolicy: 'no-cache',
  })
}

export const quickUrlValidation = async (
  urlList: string[],
): Promise<boolean | any> => {
  const query = gql`
    query QuickUrlValidation($urlList: [String!]!) {
      track {
        quickUrlValidation(urlList: $urlList) {
          validationResults {
            statusCode
            testUrl
          }
        }
      }
    }
  `
  return client.query({ query, variables: { urlList } })
}

export const validateShortLinkCandidate = async (
  testCandidate: string,
): Promise<boolean | any> => {
  const query = gql`
    query ValidateShortLinkCandidate($testCandidate: String!) {
      track {
        validateShortLinkCandidate(testCandidate: $testCandidate) {
          availableUntil
          isAvailable
          shortLinkID
        }
      }
    }
  `
  return client.query({
    query,
    variables: { testCandidate },
    fetchPolicy: 'no-cache',
  })
}

export const intensiveUrlValidation = async (
  urlList: string[],
): Promise<boolean | any> => {
  const query = gql`
    query IntensiveUrlValidation($urlList: [String!]!) {
      track {
        intensiveUrlValidation(urlList: $urlList) {
          validationResults {
            badUrl
            clickedCookieConsent
            noAnalyticsTag
            redirectedLandingPage
            slowLandingPage
            statusCode
            testUrl
            validatorReturned
          }
        }
      }
    }
  `
  return client.query({ query, variables: { urlList } })
}

interface SendFeedbackData {
  message: string
  page: string
  feedbackContext: {
    feedbackPrompt: string
    feedbackResponse: string
  }
}

export const sendFeedback = async (data: SendFeedbackData): Promise<any> => {
  const { message, page, feedbackContext } = data

  const mutation = gql`
    mutation SendFeedback(
      $message: String!
      $page: String!
      $feedbackContext: FeedbackContext!
    ) {
      userAccountSettings {
        sendFeedback(
          message: $message
          page: $page
          feedbackContext: $feedbackContext
        ) {
          email
          fullName
          curAccountID
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { message, page, feedbackContext },
  })
}

interface GetAccountDataSourceOut {
  [key: string]: {
    accountID: string
    dataSource: {
      kind: DataSource
      connected: boolean
    }
  }
}

// * New stuff
export const getAccountDataSource = async (
  accountIDs: string[],
): Promise<ApolloQueryResult<GetAccountDataSourceOut>> => {
  let accountQuery = ``
  const nanoid = customAlphabet(nanoIdCharacters, 10)

  accountIDs.forEach((accountID) => {
    accountQuery += `
        ${nanoid()}:account(
            accountID: "${accountID}"
        ) {
            accountID
            dataSource {
              kind
              connected
            }
          }
      `
  })

  const query = gql`
  query GetAccountDataSource {
      ${accountQuery}
    }
  `

  return client.query({ query })
}

export const getEnterpriseBillingDetailGQL = async (): Promise<any> => {
  const query = gql`
    query GetEnterpriseBillingDetailGQL {
      accountSettings {
        enterpriseBillingDetail {
          auditAvailable
          autoRenew
          billingUserList
          businessDivision
          contractEndDate
          companyName
          contractStartDate
          customTandC
          domains
          domainLimit
          explainAvailable
          legalEntityName
          reportAvailable
          sponsorList {
            email
            name
          }
          totalContractValue
          trackAvailable
          userLimit
          accountManagerList {
            email
            name
          }
        }
      }
    }
  `
  return client.query({ query })
}
const singleReportQuery = `applyFilters {
            dimensionName
            dimensionOptions
          }
          availableTo
          createdBy
          createdByID
          dateCreated
          endDate
          lastEditedBy
          lastEditedByID
          lastEditedDate
          matchType
          reportName
          reportTitle
          savedReportID
          selectedMetric
          stackDimension
          startDate
          `
const listSavedLinkPerformanceReportsQuery = `hiddenReportList
          savedReports {
            ${singleReportQuery}
          }
          userPreferredOrder`

export const listSavedLinkPerformanceReportsGQL = async (): Promise<any> => {
  const query = gql`
    query ListSavedLinkPerformanceReportsGQL {
      report {
        listSavedLinkPerformanceReports {
          ${listSavedLinkPerformanceReportsQuery}
        }
      }
    }
  `
  return client.query({ query })
}

interface SaveLinkPerformanceReportData {
  filterList: {
    dimensionName: string
    dimensionOptions: string[]
  }[]
  startDate: string
  endDate: string
  matchType: string
  availableTo: 'company' | 'account' | 'user'
  stackDimension: string
  selectedMetric: string
  reportTitle: string
  reportName: string
}

export const saveLinkPerformanceReport = async (
  data: SaveLinkPerformanceReportData,
): Promise<any> => {
  const mutation = gql`
    mutation SaveLinkPerformanceReport(
      $reportName: String!
      $reportTitle: String!
      $availableTo: String!
      $matchType: String!
      $selectedMetric: String!
      $startDate: String!
      $endDate: String
      $stackDimension: String!
      $filterList: [DimensionFilter!]
    ) {
      report {
        saveLinkPerformanceReport(
          reportName: $reportName
          reportTitle: $reportTitle
          availableTo: $availableTo
          matchType: $matchType
          selectedMetric: $selectedMetric
          startDate: $startDate
          endDate: $endDate
          stackDimension: $stackDimension
          filterList: $filterList
        ) {
          ${singleReportQuery}
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { ...data },
  })
}

interface UpdateLinkPerformanceUserPrefsData {
  newOrder: string[]
}

export const updateLinkPerformanceUserPrefs = async (
  data: UpdateLinkPerformanceUserPrefsData,
): Promise<any> => {
  const mutation = gql`
    mutation UpdateLinkPerformanceUserPrefs(
      $newOrder: [String!] 
    ) {
      report {
        updateLinkPerformanceUserPrefs(
          newOrder: $newOrder
        ) {
          ${listSavedLinkPerformanceReportsQuery}
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { ...data },
  })
}

interface RemoveSavedLinkPerformanceReportData {
  savedReportID: string
}

export const removeSavedLinkPerformanceReport = async (
  data: RemoveSavedLinkPerformanceReportData,
): Promise<any> => {
  const mutation = gql`
    mutation RemoveSavedLinkPerformanceReport(
      $savedReportID: String!
    ) {
      report {
         removeSavedLinkPerformanceReport(
          savedReportID: $savedReportID
        ) {
          ${listSavedLinkPerformanceReportsQuery}
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { ...data },
  })
}

interface UpdateSavedLinkPerformanceReportData {
  availableTo: 'company' | 'account' | 'user'
  reportTitle: string
  reportName: string
  savedReportID: string
  selectedMetric: string
  stackDimension: string
  startDate: string
  endDate: string
  matchType: string
  filterList: {
    dimensionName: string
    dimensionOptions: string
  }[]
}

export const updateSavedLinkPerformanceReport = async (
  data: UpdateSavedLinkPerformanceReportData,
): Promise<any> => {
  const mutation = gql`
    mutation UpdateSavedLinkPerformanceReport(
      $reportName: String
      $reportTitle: String
      $availableTo: String
      $savedReportID: String!
      $selectedMetric: String
      $stackDimension: String
      $startDate: String
      $endDate: String
      $matchType: String
      $filterList: [DimensionFilter!]
    ) {
      report {
        updateSavedLinkPerformanceReport(
          reportName: $reportName
          reportTitle: $reportTitle
          availableTo: $availableTo
          savedReportID: $savedReportID
          selectedMetric: $selectedMetric
          stackDimension: $stackDimension
          startDate: $startDate
          endDate: $endDate
          matchType: $matchType
          filterList: $filterList
        ) {
          ${singleReportQuery}
        }
      }
    }
  `

  return client.mutate({
    mutation,
    variables: { ...data },
  })
}
